import { useState, useMemo } from 'react';
import { useList, useGo, useCustomMutation, useInvalidate, type CrudFilter } from '@refinedev/core';
import { Ban, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/hooks/use-confirm';
import {
  DataTable,
  TableToolbar,
  FilterSelect,
  FilterDateRange,
  type Column,
  type SortState,
  type BulkAction,
  type FilterOption,
} from '@/components/tables';
import type { Subscription, SubscriptionPlan } from './types';
import { BulkTagsDialog } from '@/components/bulk-tags-dialog';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'PENDING', label: 'Ожидает' },
  { value: 'ACTIVE', label: 'Активна' },
  { value: 'EXPIRED', label: 'Истекла' },
  { value: 'CANCELLED', label: 'Отменена' },
];

const STATUS_BADGE_MAP: Record<string, { label: string; className: string }> = {
  active: { label: 'Активна', className: 'bg-green-500/10 text-green-600 border-green-500/20' },
  pending: { label: 'Ожидает', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
  expired: { label: 'Истекла', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
  cancelled: { label: 'Отменена', className: 'bg-red-500/10 text-red-600 border-red-500/20' },
};

function formatDateShort(value: string | Date | null | undefined): string {
  if (!value) {return '\u2014';}
  return new Date(value).toLocaleDateString('ru-RU');
}

function getUserDisplay(sub: Subscription): string {
  if (!sub.user) {return `#${sub.userId}`;}
  const name = [sub.user.firstName, sub.user.lastName].filter(Boolean).join(' ');
  if (name) {return sub.user.username ? `${name} (@${sub.user.username})` : name;}
  return sub.user.username ? `@${sub.user.username}` : `#${sub.userId}`;
}

function getPlanDisplay(sub: Subscription): string {
  return sub.subscriptionPlan?.code ?? sub.plan?.code ?? '—';
}

export function SubscriptionsList() {
  useScrollRestoration();
  const go = useGo();
  const { confirm, ConfirmationDialog } = useConfirm();
  const [page, setPage] = usePersistedState('subscriptions:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('subscriptions:sort', { field: 'id', order: 'desc' });
  const [search, setSearch] = usePersistedState('subscriptions:search', '');

  // Фильтры
  const [statusFilter, setStatusFilter] = usePersistedState('subscriptions:statusFilter', 'all');
  const [planFilter, setPlanFilter] = usePersistedState('subscriptions:planFilter', 'all');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  const pageSize = 20;

  // Загрузка планов подписки для фильтра
  const plansResult = useList<SubscriptionPlan>({
    resource: 'subscription-plans',
    pagination: { mode: 'off' },
  });
  const plans = plansResult.result.data ?? [];
  const planOptions: FilterOption[] = plans.map((p) => ({
    value: String(p.id),
    label: p.name ?? p.code,
  }));

  // Формируем фильтры для запроса
  const filters = useMemo<CrudFilter[]>(() => {
    const result: CrudFilter[] = [];

    if (search) {
      result.push({ field: 'search', operator: 'contains', value: search });
    }
    if (statusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq', value: statusFilter });
    }
    if (planFilter !== 'all') {
      result.push({ field: 'subscriptionPlanId', operator: 'eq', value: planFilter });
    }
    if (dateFrom) {
      result.push({ field: 'startDateFrom', operator: 'eq', value: dateFrom.toISOString() });
    }
    if (dateTo) {
      result.push({ field: 'startDateTo', operator: 'eq', value: dateTo.toISOString() });
    }

    return result;
  }, [search, statusFilter, planFilter, dateFrom, dateTo]);

  const activeFiltersCount = [
    statusFilter !== 'all',
    planFilter !== 'all',
    dateFrom !== undefined,
    dateTo !== undefined,
  ].filter(Boolean).length;

  const listResult = useList<Subscription>({
    resource: 'subscriptions',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  // Bulk cancel
  const { mutate: bulkCancel } = useCustomMutation();
  const invalidate = useInvalidate();

  // Tags bulk dialog state
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [tagsDialogTargets, setTagsDialogTargets] = useState<Subscription[]>([]);

  const handleBulkCancel = async (rows: Subscription[]) => {
    const confirmed = await confirm({
      title: `Отменить ${rows.length} подписок?`,
      description: 'Выбранные подписки будут отменены. Это действие нельзя будет отменить.',
      variant: 'destructive',
      confirmText: 'Отменить подписки',
    });
    if (!confirmed) {return;}
    const ids = rows.map((r) => r.id);
    bulkCancel({
      url: '/subscriptions/bulk/cancel',
      method: 'post',
      values: { ids },
    }, {
      onSuccess: () => {
        invalidate({ resource: 'subscriptions', invalidates: ['list'] });
      },
    });
  };

  const columns: Column<Subscription>[] = [
    {
      key: 'userId',
      header: 'Пользователь',
      render: (_, sub) => (
        <button
          type="button"
          className="text-left text-primary hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            go({ to: `/users/show/${sub.userId}` });
          }}
        >
          {getUserDisplay(sub)}
        </button>
      ),
    },
    {
      key: 'subscriptionPlanId',
      header: 'План',
      render: (_, sub) => {
        const planId = sub.subscriptionPlanId ?? sub.planId;
        return (
          <button
            type="button"
            className="text-left text-primary hover:underline text-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (planId) {go({ to: `/subscription-plans/show/${planId}` });}
            }}
          >
            {getPlanDisplay(sub)}
          </button>
        );
      },
    },
    {
      key: 'status',
      header: 'Статус',
      render: (value) => {
        const status = String(value).toLowerCase();
        const badge = STATUS_BADGE_MAP[status] ?? { label: String(value), className: '' };
        return (
          <Badge variant="outline" className={badge.className}>
            {badge.label}
          </Badge>
        );
      },
    },
    {
      key: 'startDate',
      header: 'Период',
      sortable: true,
      render: (_, sub) => {
        const start = sub.startDate;
        const end = sub.endDate;
        const status = String(sub.status).toLowerCase();

        if (!start && !end) {
          if (status === 'active') {
            return <span className="text-muted-foreground">Бессрочно</span>;
          }
          return null;
        }

        if (!end && status === 'active') {
          return (
            <span className="text-sm">
              {formatDateShort(start)} — <span className="text-muted-foreground">Бессрочно</span>
            </span>
          );
        }

        return (
          <span className="text-sm">
            {formatDateShort(start)} — {formatDateShort(end)}
          </span>
        );
      },
    },
    {
      key: 'isFamilyOwner',
      header: 'Семейная',
      render: (_, sub) => {
        if (!sub.isFamilyOwner) {return <span className="text-muted-foreground">{'\u2014'}</span>;}
        return (
          <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20">
            Семейная ({sub.currentFamilySlots} мест)
          </Badge>
        );
      },
    },
  ];

  const bulkActions: BulkAction<Subscription>[] = [
    {
      label: 'Теги',
      icon: <Tag className="h-4 w-4" />,
      onClick: (rows) => {
        setTagsDialogTargets(rows);
        setTagsDialogOpen(true);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Отменить',
      icon: <Ban className="h-4 w-4" />,
      onClick: handleBulkCancel,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const handleResetFilters = () => {
    setStatusFilter('all');
    setPlanFilter('all');
    setDateFrom(undefined);
    setDateTo(undefined);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {ConfirmationDialog}
      <div className="flex justify-end">
        <Button onClick={() => go({ to: '/subscriptions/create' })}>
          Создать подписку
        </Button>
      </div>

      <TableToolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Поиск по имени, username..."
        filtersCount={activeFiltersCount}
        onResetFilters={handleResetFilters}
        filters={
          <>
            <FilterSelect
              label="Статус"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
              options={STATUS_OPTIONS}
            />
            <FilterSelect
              label="План подписки"
              value={planFilter}
              onChange={(value) => {
                setPlanFilter(value);
                setPage(1);
              }}
              options={planOptions}
            />
            <FilterDateRange
              label="Дата начала"
              from={dateFrom}
              to={dateTo}
              onFromChange={(d) => {
                setDateFrom(d);
                setPage(1);
              }}
              onToChange={(d) => {
                setDateTo(d);
                setPage(1);
              }}
            />
          </>
        }
      />

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(sub) => go({ to: `/subscriptions/show/${sub.id}` })}
        emptyMessage="Нет подписок"
        selectable
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <BulkTagsDialog
        open={tagsDialogOpen}
        onOpenChange={(open) => {
          setTagsDialogOpen(open);
          if (!open) { setTagsDialogTargets([]); }
        }}
        userIds={tagsDialogTargets.map((s) => s.userId)}
        selectionLabel={
          tagsDialogTargets.length === 1
            ? getUserDisplay(tagsDialogTargets[0])
            : `${tagsDialogTargets.length} подписок`
        }
        onSuccess={() => invalidate({ resource: 'subscriptions', invalidates: ['list'] })}
      />
    </div>
  );
}
